/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormHelperText,
  CircularProgress,
  FormControlLabel
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { httpRequest } from 'src/services/httpRequest';

import {
  BidFormData,
  BidFormErrors,
  TypeOption,
  CategoryOption,
  DepartmentOption,
  StatusOption
} from './index';
import { DatePicker } from '@mui/x-date-pickers';

// Update the BidFormData interface to include award-related fields and addendum checkbox
interface ExtendedBidFormData extends BidFormData {
  awardedToVendor?: string;
  awardedToContact?: string;
  // Make sure this matches the requirement in BidFormData (using required property)
  awardedToContactId: number;
  bidResult?: File | null;
  bidResultUrl?: string | null;
  bidResultTitle?: string;
  bidResultUploaded?: boolean;
  bidResultError?: string | null;
  bidResultIsUploading?: boolean;
  poFile?: File | null;
  poFileUrl?: string | null;
  poFileTitle?: string;
  poFileUploaded?: boolean;
  poFileError?: string | null;
  poFileIsUploading?: boolean;
  poNumber: string; // Make this required to match BidFormData
  awardAmount?: number;
  isAddendum: boolean; // Add the addendum checkbox field
}

// Update the errors interface to include award-related error fields
interface ExtendedBidFormErrors extends BidFormErrors {
  awardedToVendor?: string;
  awardedToContact?: string;
  bidResult?: string;
  poNumber?: string;
  poFile?: string;
  awardDate?: string; // Add award date validation error
}

interface BidDetailsFormProps {
  formData: ExtendedBidFormData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedBidFormData>>;
  errors: ExtendedBidFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<ExtendedBidFormErrors>>;
  types: TypeOption[];
  categories: CategoryOption[];
  departments: DepartmentOption[];
  statuses: StatusOption[];
  isGeneratingContractNo: boolean;
  onBidTypeChange: (selectedType: string) => Promise<void>;
  handleFileUpload: (index: number, file: File) => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  isFormDisabled: any;
  originalStatus: string;
}

const BidChildDetailsForm: React.FC<BidDetailsFormProps> = ({
  formData,
  setFormData,
  errors,
  setErrors,
  types,
  categories,
  departments,
  statuses,
  isGeneratingContractNo,
  onBidTypeChange,
  isFormDisabled,
  originalStatus
}) => {
  // Extract bidId from URL
  const { bidId } = useParams<{ bidId: string }>();

  // State for vendors from API
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [, setVendorError] = useState<string | null>(null);

  // State for contacts from API
  const [contacts, setContacts] = useState<
    { id: string; name: string; userId?: number; displayName: string }[]
  >([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [, setContactError] = useState<string | null>(null);
  const [previousAdminNotes, setPreviousAdminNotes] = useState<string | null>(
    null
  );
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  const filteredStatus = statuses?.filter((item) => {
    if (originalStatus === 'Open') {
      return item.Status !== 'Draft';
    } else {
      return true;
    }
  });

  // Add a ref to track which vendors we've already fetched contacts for
  const fetchedVendorsRef = useRef<Set<string>>(new Set());

  // Helper function to validate award date
  const validateAwardDate = (
    awardDate: Date | null,
    bidDeadline: Date | null
  ): string | null => {
    if (!awardDate || !bidDeadline) {
      return null; // No validation if either date is missing
    }

    if (awardDate <= bidDeadline) {
      return 'Award date must be greater than bid deadline';
    }

    return null;
  };

  // Define fetchVendors using useCallback to avoid dependency issues
  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    setVendorError(null);

    try {
      const response = await httpRequest(
        'GET',
        '/auth/vendors_information',
        {},
        {}
      );

      if (response.status === 'success' && response.data?.BusinessDetails) {
        // Transform the API response to the format needed for the select component
        const vendorsList = response.data.BusinessDetails.map(
          (vendor: { Business: string }, index: number) => ({
            id: `vendor-${index}`,
            name: vendor.Business
          })
        );

        setVendors(vendorsList);
      } else {
        console.error('Unexpected vendor response format:', response);
        setVendorError('Failed to load vendors. Unexpected response format.');
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendorError('Failed to load vendors. Please try again.');
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  // Function to fetch contacts based on selected vendor and bidId
  const fetchContacts = useCallback(
    async (vendorName: string) => {
      if (!vendorName) return;

      if (fetchedVendorsRef.current.has(vendorName)) {
        return;
      }

      if (loadingContacts) {
        return;
      }

      setLoadingContacts(true);
      setContactError(null);
      setContacts([]);

      try {
        fetchedVendorsRef.current.add(vendorName);

        const url = `/auth/vendors_information?Business=${encodeURIComponent(
          vendorName
        )}${bidId ? `&BidID=${bidId}` : ''}`;

        const response = await httpRequest('GET', url, {}, {});

        if (response.status === 'success') {
          if (
            response.data?.VendorDetails &&
            Array.isArray(response.data.VendorDetails)
          ) {
            const contactsList = response.data.VendorDetails.map(
              (contact: any) => ({
                id: `contact-${contact.UserID || contact.FullNameWithEmail}`,
                name:
                  contact.FullNameWithEmail ||
                  `${contact.FirstName} ${contact.LastName}`,
                userId: contact.UserID,
                displayName:
                  contact.FullNameWithEmail ||
                  `${contact.FirstName} ${contact.LastName}`
              })
            );

            setContacts(contactsList);

            if (contactsList.length > 0) {
              const firstContact = contactsList[0];
              setFormData((prevData) => ({
                ...prevData,
                awardedToContact: firstContact.name,
                awardedToContactId: firstContact.userId || 0
              }));
            }
          } else {
            setContacts([]);
          }
        } else {
          setContactError('Failed to load contacts. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setContactError('Failed to load contacts. Please try again.');
        fetchedVendorsRef.current.delete(vendorName);
      } finally {
        setLoadingContacts(false);
      }
    },
    [bidId, loadingContacts]
  );

  const handleBidTypeChange = (e: SelectChangeEvent) => {
    onBidTypeChange(e.target.value);
    if (errors.bidType) {
      setErrors((prev) => ({ ...prev, bidType: '' }));
    }
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    const newStatus = e.target.value;
    setFormData({ ...formData, status: newStatus, isAddendum: false });
    if (errors.status) {
      setErrors((prev) => ({ ...prev, status: '' }));
    }

    // If status changed to "Awarded", fetch vendors if not already fetched
    if (newStatus === 'Awarded' && vendors.length === 0 && !loadingVendors) {
      fetchVendors();
    }
  };

  // Handle addendum checkbox change
  const handleAddendumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFormData({
      ...formData,
      isAddendum: checked,
      // If checked, clear the admin notes, otherwise restore the previous value
      adminNotes: checked ? '' : previousAdminNotes || '' // Use the saved value when unchecked
    });

    if (checked) {
      // If checked, clear the admin notes
      setPreviousAdminNotes(formData.adminNotes || ''); // Save current admin notes before clearing
    }
  };

  // Check if status is "Awarded" or "Close"
  const isAwarded = formData.status === 'Awarded';
  const isClosed = formData.status === 'Close';
  const showAwardDetails = isAwarded || isClosed;
  // Check if status is "Draft" for Go Live Date enablement
  const isDraft = formData.status === 'Draft';
  // Check if status is "Open" for addendum checkbox
  const isOpen = formData.status === 'Open';

  // Unified useEffect to handle both vendor and contact fetching
  useEffect(() => {
    // Fetch vendors once when status is "Awarded"

    // Handle pre-populated vendor data during initial load
    if (
      isAwarded &&
      formData.awardedToVendor &&
      !loadingContacts &&
      contacts.length === 0 &&
      !fetchedVendorsRef.current.has(formData.awardedToVendor)
    ) {
      // eslint-disable-next-line no-console
      console.log(
        `Initial load: Fetching contacts for ${formData.awardedToVendor}`
      );
      fetchContacts(formData.awardedToVendor);
    }
  }, [
    isAwarded,
    vendors.length,
    loadingVendors,
    fetchVendors,
    formData.awardedToVendor,
    loadingContacts,
    contacts.length,
    fetchContacts
  ]);

  useEffect(() => {
    fetchVendors();
  }, []);

  // Add cleanup function to reset the fetched vendors when the component unmounts
  useEffect(() => {
    return () => {
      fetchedVendorsRef.current.clear();
    };
  }, []);

  // Handle award date change with validation
  const handleAwardDateChange = (newValue: Date | null) => {
    setFormData({ ...formData, awardDate: newValue });

    // Validate award date against bid deadline
    const validationError = validateAwardDate(newValue, formData.bidDeadline);
    if (validationError) {
      setErrors((prev) => ({ ...prev, awardDate: validationError }));
    } else {
      setErrors((prev) => ({ ...prev, awardDate: '' }));
    }
  };

  // Handle bid deadline change and revalidate award date
  const handleBidDeadlineChange = (newValue: Date | null) => {
    setFormData({ ...formData, bidDeadline: newValue });

    if (errors.bidDeadline) {
      setErrors((prev) => ({ ...prev, bidDeadline: '' }));
    }

    // Revalidate award date if it exists
    if (formData.awardDate) {
      const validationError = validateAwardDate(formData.awardDate, newValue);
      if (validationError) {
        setErrors((prev) => ({ ...prev, awardDate: validationError }));
      } else {
        setErrors((prev) => ({ ...prev, awardDate: '' }));
      }
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.bidType} required>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.bidType || ''}
            onChange={handleBidTypeChange}
            label="Type"
            required
            disabled
          >
            {types.map((type) => (
              <MenuItem key={type.TypeID} value={type.Type}>
                {type.Type}
              </MenuItem>
            ))}
          </Select>
          {errors.bidType && <FormHelperText>{errors.bidType}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contract No."
          disabled
          value={formData.contractNo || ''}
          onChange={(e) => {
            setFormData({ ...formData, contractNo: e.target.value });
            if (errors.contractNo) {
              setErrors((prev) => ({ ...prev, contractNo: '' }));
            }
          }}
          error={!!errors.contractNo}
          helperText={errors.contractNo}
          InputProps={{
            endAdornment: isGeneratingContractNo ? (
              <CircularProgress size={20} />
            ) : null,
            readOnly: isGeneratingContractNo
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={formData.title || ''}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            if (errors.title && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, title: '' }));
            }
          }}
          error={!!errors.title}
          helperText={errors.title}
          required
          disabled
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth error={!!errors.categories} required>
          <InputLabel>Categories</InputLabel>
          <Select
            open={openCategorySelect}
            onOpen={() => setOpenCategorySelect(true)}
            onClose={() => setOpenCategorySelect(false)}
            disabled
            multiple
            value={formData.categories || []}
            onChange={(e) => {
              const newCategories =
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;

              // Handle "Select All" logic
              if (newCategories.includes('SELECT_ALL')) {
                const allCategoryNames = categories.map(
                  (cat) => cat.CategoryName
                );
                const currentCategories = formData.categories || [];
                const isAllSelected =
                  currentCategories.length === categories.length;

                // If all items are selected, deselect all. Otherwise, select all.
                const finalCategories = isAllSelected ? [] : allCategoryNames;

                setFormData({
                  ...formData,
                  categories: finalCategories
                });

                if (errors.categories && finalCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              } else {
                setFormData({
                  ...formData,
                  categories: newCategories
                });

                if (errors.categories && newCategories.length > 0) {
                  setErrors((prev) => ({ ...prev, categories: '' }));
                }
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 350,
                  borderRadius: 1.5,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  marginTop: 1
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            input={<OutlinedInput label="Categories" />}
            renderValue={(selected) => selected.join(', ')}
            required
          >
            {/* Select All Option */}
            <MenuItem value="SELECT_ALL">
              <Checkbox
                checked={
                  (formData.categories || []).length === categories.length
                }
                indeterminate={
                  (formData.categories || []).length > 0 &&
                  (formData.categories || []).length < categories.length
                }
              />
              <ListItemText
                primary="Select All"
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              />
            </MenuItem>

            {/* Individual Category Options */}
            {categories.map((category) => (
              <MenuItem key={category.CategoryID} value={category.CategoryName}>
                <Checkbox
                  checked={
                    (formData.categories || []).indexOf(category.CategoryName) >
                    -1
                  }
                />
                <ListItemText primary={category.CategoryName} />
              </MenuItem>
            ))}

            <MenuItem
              disableRipple
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #ddd',
                mt: 1,
                pt: 1
              }}
              onClick={(e) => e.stopPropagation()} // ✅ Prevent MenuItem click
            >
              <button
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ Prevent event bubbling
                  setOpenCategorySelect(false);
                }}
              >
                Apply
              </button>
            </MenuItem>
          </Select>
          {errors.categories && (
            <FormHelperText>{errors.categories}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.department} required>
          <InputLabel>Department</InputLabel>
          <Select
            disabled
            value={formData.department || ''}
            onChange={(e: SelectChangeEvent) => {
              setFormData({ ...formData, department: e.target.value });
              if (errors.department && e.target.value) {
                setErrors((prev) => ({ ...prev, department: '' }));
              }
            }}
            label="Department"
            required
          >
            {departments.map((dept) => (
              <MenuItem key={dept.DepartmentID} value={dept.DepartmentName}>
                {dept.DepartmentName}
              </MenuItem>
            ))}
          </Select>
          {errors.department && (
            <FormHelperText>{errors.department}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.status} required>
          <InputLabel>Status</InputLabel>
          <Select
            disabled
            value={formData.status || ''}
            onChange={handleStatusChange}
            label="Status"
            required
          >
            {filteredStatus?.map((status) => (
              <MenuItem key={status.StatusID} value={status.Status}>
                {status.Status}
              </MenuItem>
            ))}
          </Select>
          {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Addendum Checkbox - Only show when status is "Open" */}
      {isOpen && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isAddendum || false}
                onChange={handleAddendumChange}
                disabled={isFormDisabled}
              />
            }
            label="Check if any addendum in bid"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={4} mt={showAwardDetails && !isAwarded ? 2 : 0}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disabled={!isDraft} // Only enable when status is "Draft" and form is not disabled
            label="Ad Date"
            value={formData.adDate}
            onChange={(newValue) =>
              setFormData({ ...formData, adDate: newValue })
            }
            slotProps={{
              textField: {
                fullWidth: true
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            disabled
            label="Opening Date"
            value={formData.openingDate}
            onChange={handleBidDeadlineChange}
            slotProps={{
              textField: {
                fullWidth: true,
                // error: !!errors.bidDeadline,
                // helperText: errors.bidDeadline,
                required: true
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disabled
            label="Effective Date"
            value={formData.effectiveDate}
            onChange={handleAwardDateChange}
            slotProps={{
              textField: {
                fullWidth: true
                // error: !!errors.awardDate,
                // helperText: errors.awardDate
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <TextField
          disabled={isFormDisabled}
          fullWidth
          multiline
          rows={6}
          label="Description"
          value={formData.description || ''}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          disabled={isFormDisabled}
          fullWidth
          multiline
          rows={4}
          label="Admin Notes"
          value={formData.adminNotes || ''}
          onChange={(e) =>
            setFormData({ ...formData, adminNotes: e.target.value })
          }
        />
      </Grid>
    </Grid>
  );
};

export default BidChildDetailsForm;
